import classNames from 'classnames';

import { Size, useWindowSize } from '@/hooks/useWindowSize';
import { Article } from '@/types/Article';

import ArticleCard, { ArticleCardType } from '../article-card/article-card';
import ArticleGridSkeleton from '../skeleton-load/article-grid-skeleton/article-grid-skeleton';
import styles from './article-grid.module.scss';

export enum TEMPLATE_TYPE {
  DEFAULT = 'default',
  THREE_LINE = 'Three Line',
  ONE_LINE = 'One Line',
  SIX_LINE = 'Six Line',
}

interface ArticleGridProps {
  articles: Article[];
  setOffsetArticle: (count: number) => void;
  offsetArticle: number;
  isLoadingMoreArticles: boolean;
  templateType?: TEMPLATE_TYPE;
  itemPerPage?: number;
  showLoadMore?: boolean;
}

const CardType = {
  SMALL: 'SMALL',
  LARGE: 'LARGE',
};

const templatesDefault = [
  [CardType.LARGE, CardType.SMALL, CardType.SMALL],
  [CardType.SMALL, CardType.SMALL, CardType.LARGE],
];

const templatesThreeLine = [
  [CardType.LARGE, CardType.SMALL, CardType.SMALL],
  [CardType.SMALL, CardType.SMALL, CardType.SMALL, CardType.SMALL],
  [CardType.SMALL, CardType.SMALL, CardType.LARGE],
];

const templatesSixLine = [
  [CardType.LARGE, CardType.SMALL, CardType.SMALL],
  [CardType.SMALL, CardType.SMALL, CardType.SMALL, CardType.SMALL],
  [CardType.SMALL, CardType.SMALL, CardType.LARGE],
  [CardType.SMALL, CardType.SMALL, CardType.SMALL, CardType.SMALL],
  [CardType.SMALL, CardType.LARGE, CardType.SMALL],
  [CardType.SMALL, CardType.SMALL, CardType.SMALL, CardType.SMALL],
];

const templatesOneLine = [[CardType.LARGE, CardType.SMALL, CardType.SMALL]];

const tabletTemplate = [
  [CardType.LARGE, CardType.SMALL],
  [CardType.SMALL, CardType.SMALL, CardType.SMALL],
  [CardType.SMALL, CardType.LARGE],
  [CardType.SMALL, CardType.SMALL, CardType.SMALL],
];

export const getTemplate = (
  templateType: TEMPLATE_TYPE | undefined,
  windowSize: Size
): string[][] => {
  let template;
  switch (templateType) {
    case TEMPLATE_TYPE.THREE_LINE:
      template = templatesThreeLine;
      break;
    case TEMPLATE_TYPE.ONE_LINE:
      template = templatesOneLine;
      break;
    case TEMPLATE_TYPE.SIX_LINE:
      template = templatesSixLine;
      break;
    default:
      template = templatesDefault;
      if ((windowSize.width || 0) <= 1200) {
        template = tabletTemplate;
      }
  }
  return template;
};

const ArticleGrid = (props: ArticleGridProps) => {
  const {
    articles,
    setOffsetArticle,
    offsetArticle,
    isLoadingMoreArticles,
    templateType,
    itemPerPage = 6,
    showLoadMore = true,
  } = props;
  const windowSize = useWindowSize();

  const handleCards = () => {
    const maxTotalRows = articles.length;
    const cards = [];
    let articleIndex = 0;

    const cardTemplate = getTemplate(templateType, windowSize);

    for (let rowNumber = 0; rowNumber < maxTotalRows; rowNumber += 1) {
      const templateIndex = rowNumber % cardTemplate.length;
      const templateRow = cardTemplate[templateIndex];
      if (articleIndex >= maxTotalRows) {
        break;
      }
      for (let colNumber = 0; colNumber < templateRow.length; colNumber++) {
        const cartType = templateRow[colNumber];
        if (articleIndex >= maxTotalRows) {
          break;
        }

        const article = articles[articleIndex];
        if (cartType === CardType.SMALL) {
          cards.push(
            <ArticleCard
              key={`article_card_${article.id}`}
              type={ArticleCardType.Small}
              article={article}
            />
          );
        }
        if (cartType === CardType.LARGE) {
          cards.push(
            <div
              className={styles.articleLarge}
              key={`article_card_${article.id || Math.random()}`}
            >
              <ArticleCard type={ArticleCardType.Large} article={article} />
            </div>
          );
        }

        articleIndex += 1;
      }
    }

    return cards;
  };

  const handleLoadMoreArticle = () => {
    const count = offsetArticle + itemPerPage;
    setOffsetArticle(count);
  };

  const renderArticleContent = () => {
    if (articles.length === 0 && !isLoadingMoreArticles) {
      return null;
    }

    if (articles.length === 0) {
      return <ArticleGridSkeleton />;
    }

    return <div className={styles.articlesContainer}>{handleCards().map((card) => card)}</div>;
  };

  const renderButtonLoadMore = () => {
    if (articles.length < itemPerPage || offsetArticle > articles.length) {
      return null;
    }

    return (
      <div
        className={classNames(styles.buttonLoadMore, {
          [styles.loading]: isLoadingMoreArticles,
          [styles.loaded]: !isLoadingMoreArticles,
        })}
        onClick={() => handleLoadMoreArticle()}
      >
        <div className={styles.buttonLoadMoreContent}>
          <span>Load more</span>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.articleGrid}>
      {renderArticleContent()}
      <div className={styles.loadMoreContainer}>
        {showLoadMore && renderButtonLoadMore()}
        <div
          className={classNames(styles.spinnerLoadMore, {
            [styles.loading]: isLoadingMoreArticles,
            [styles.loaded]: !isLoadingMoreArticles,
          })}
        ></div>
      </div>
    </div>
  );
};

export default ArticleGrid;
